/**
 * Sole owner of writes to the flat `notifications` storage array —
 * every `webStorage.set` call that includes the `notifications` key
 * lives in this file. Two families of helpers:
 *
 * REMOVE / PRUNE (pre-existing):
 *   - `dropNotificationFromStorage` — single msgId drop. Callers:
 *     `messages.ts` `dismissNotification` (user-initiated dismiss via
 *     the close button), `sse.ts` `dropFromStorage` (server-pushed
 *     dismiss event when the paired Android dismisses upstream).
 *   - `dropNotificationsFromStorage` — bulk variant backing
 *     `messages.ts` `dismissAllNotifications` (Clear All).
 *   - `pruneExpiredFromStorage` — TTL auto-dismiss for server-origin
 *     (`/v1/send`) notifications.
 *
 *   Sharing keeps the unread-count math + not-found short-circuit in
 *   one place; if the rules ever change (e.g. multi-tab broadcast,
 *   undo window) both flows pick it up automatically.
 *
 * APPEND / MERGE (incoming pipeline):
 *   - `applyIncomingNotification` — the device-mirrored flat-append
 *     path: `replacedMsgId`/`(appPackage,title)` update-merge, the
 *     `NOTIFICATION_CAP` slice, the write-once appIcon cache, and the
 *     batched `{notifications, unreadCount, lastSeqId, appIconCache?}`
 *     write. Called from `notifications.ts`'s `processIncomingMessage`.
 *   - `applyServerOriginNotification` — the public-API
 *     (`senderDeviceId === "server"`) path: no merge (every send is a
 *     fresh card), same cap + batched write shape minus the icon
 *     cache. Called from `notifications.ts`'s
 *     `handleServerOriginMessage`.
 *
 *   Both callers keep their own decrypt/dedup/display-gate/logging —
 *   only the storage mutation lives here.
 */

import {
  type DecryptedNotification,
  isNotificationExpired,
  pruneExpiredNotifications,
  type ReflectoStorage,
} from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";

/**
 * Maximum number of notifications kept in the `notifications` array.
 * Mirrors apps/extension's NOTIFICATION_CAP — keeps the storage hot
 * path bounded and the UI list cheap to render.
 */
export const NOTIFICATION_CAP = 100;

/**
 * Mirrors apps/extension/src/background/index.ts's appIcon size
 * cap. Android's NotificationListenerService caps base64 icons at
 * roughly 8KB raw; 12_000 chars of base64 is ~9KB raw with headroom.
 * Anything larger gets dropped from the cache (defense in depth —
 * the source of truth is the phone, but a malformed payload should
 * not be able to bloat IndexedDB unbounded).
 */
export const APP_ICON_MAX_CHARS = 12_000;

/**
 * Drop a single notification from the flat `notifications` storage
 * array and decrement `unreadCount` if the dropped one was unread.
 *
 * Returns `true` when a matching msgId was found and removed,
 * `false` when the array didn't contain it (idempotent — caller
 * doesn't need to pre-check).
 */
export async function dropNotificationFromStorage(msgId: string): Promise<boolean> {
  const { notifications, unreadCount } = await webStorage.get("notifications", "unreadCount");
  const list: DecryptedNotification[] = notifications ?? [];
  const removed = list.find((n) => n.msgId === msgId);
  const filtered = list.filter((n) => n.msgId !== msgId);
  if (filtered.length === list.length) return false;
  const updates: Partial<ReflectoStorage> = { notifications: filtered };
  if (removed?.isRead === false) {
    updates.unreadCount = Math.max(0, (unreadCount ?? 0) - 1);
  }
  await webStorage.set(updates);
  return true;
}

/**
 * Bulk variant of `dropNotificationFromStorage` — removes every msgId in `msgIds`
 * in a SINGLE read/write. Backs "Clear All" so a large backlog doesn't fan out
 * into one IndexedDB write per card. `unreadCount` is decremented by the number
 * of removed-and-unread cards. Returns the count actually removed.
 */
export async function dropNotificationsFromStorage(msgIds: string[]): Promise<number> {
  if (msgIds.length === 0) return 0;
  const idSet = new Set(msgIds);
  const { notifications, unreadCount } = await webStorage.get("notifications", "unreadCount");
  const list: DecryptedNotification[] = notifications ?? [];
  const filtered = list.filter((n) => !idSet.has(n.msgId));
  const removedCount = list.length - filtered.length;
  if (removedCount === 0) return 0;
  const removedUnread = list.filter((n) => idSet.has(n.msgId) && n.isRead === false).length;
  const updates: Partial<ReflectoStorage> = { notifications: filtered };
  if (removedUnread > 0) {
    updates.unreadCount = Math.max(0, (unreadCount ?? 0) - removedUnread);
  }
  await webStorage.set(updates);
  return removedCount;
}

/**
 * Drop notifications whose public-API `ttl` window has elapsed (`expiresAt <=
 * now`). Server-origin (`/v1/send`) notifications carry an `expiresAt` derived
 * from the payload `ttl`; the server only embeds the hint — it does NOT delete
 * on the client's behalf, so the client must self-prune. Device-mirrored
 * (Android) notifications have no `expiresAt` and are never touched.
 *
 * Runs in the foreground page (on load + on an interval — see
 * `useExpiredNotificationPrune`). The SW has no equivalent timer; an expired
 * toast is reaped here on the next page load instead.
 *
 * `unreadCount` is decremented by the number of unread notifications that were
 * pruned, mirroring `dropNotificationFromStorage`. Writes only when something
 * actually expired, so a steady-state interval tick with no expiries is a
 * single read and no write (no spurious cross-tab storage event).
 *
 * Returns the number of notifications removed.
 */
export async function pruneExpiredFromStorage(now: number = Date.now()): Promise<number> {
  // Cheap pre-check: steady-state ticks with nothing expired stay a single read,
  // no write (no spurious cross-tab storage event).
  const initial = await webStorage.get("notifications");
  if (
    pruneExpiredNotifications(initial.notifications ?? [], now).length ===
    (initial.notifications ?? []).length
  ) {
    return 0;
  }

  // Re-read immediately before the write and re-apply the expiry filter to the
  // freshest list. StorageAdapter has no atomic update, so this narrows the
  // read→write window: a notification another tab wrote between the pre-check and
  // here is non-expired, so freshPruned keeps it rather than the snapshot stomping
  // it. The filter is deterministic (`expiresAt <= now`), so re-applying is safe.
  const { notifications, unreadCount } = await webStorage.get("notifications", "unreadCount");
  const list: DecryptedNotification[] = notifications ?? [];
  const pruned = pruneExpiredNotifications(list, now);
  const removedCount = list.length - pruned.length;
  if (removedCount === 0) return 0;

  const removedUnread = list.filter(
    (n) => isNotificationExpired(n, now) && n.isRead === false
  ).length;
  const updates: Partial<ReflectoStorage> = { notifications: pruned };
  if (removedUnread > 0) {
    updates.unreadCount = Math.max(0, (unreadCount ?? 0) - removedUnread);
  }
  await webStorage.set(updates);
  return removedCount;
}

/**
 * Snapshot fields `applyIncomingNotification` needs from the caller's single
 * `webStorage.get` — passed through rather than re-read here so the whole
 * incoming-message pipeline stays one read + one write.
 */
export interface ApplyIncomingNotificationOptions {
  /**
   * Already-built row for this message (msgId/seqId/appPackage/title read
   * off it for the merge match and the final cap slice).
   */
  next: DecryptedNotification;
  /**
   * Android's merge hint (`parsed.replacedMsgId`) — preferred match key
   * over `(appPackage, title)`.
   */
  replacedMsgId: string | undefined;
  /** Raw appIcon from the payload, before the size-cap + write-once gate below. */
  appIcon: string | undefined;
  /**
   * True when the per-device display gate resolved to `"silent"` —
   * suppresses the unread bump. `"hide"` is filtered out by the caller
   * before this is invoked.
   */
  isSilent: boolean;
  notifications: DecryptedNotification[] | undefined;
  unreadCount: number | undefined;
  appIconCache: Record<string, string> | undefined;
  lastSeqId: number | undefined;
}

/**
 * Apply one device-mirrored notification to the flat `notifications` array:
 * update-merge (or prepend), cap, write-once appIcon cache, and the single
 * batched `webStorage.set`. Mirrors apps/extension/src/background/index.ts's
 * append path (PR #378). Called once per message from `notifications.ts`'s
 * `processIncomingMessage` — never for conversation messages (those route
 * through `conversationsStore.ts` instead) and never for public-API sends
 * (see `applyServerOriginNotification` below).
 */
export async function applyIncomingNotification(
  options: ApplyIncomingNotificationOptions
): Promise<void> {
  const {
    next,
    replacedMsgId,
    appIcon,
    isSilent,
    notifications,
    unreadCount,
    appIconCache,
    lastSeqId,
  } = options;

  // Update merge — mirrors apps/extension/src/background/index.ts (PR #378).
  // When `isUpdate` is set, find the existing card by `replacedMsgId` (preferred,
  // matching either the original `msgId` or `latestAndroidMsgId` for chained
  // updates) or by `(appPackage, title)` (fallback for older Android builds and
  // FIFO-evicted prior msgIds). On match: replace in place keeping the original
  // `msgId` so dismiss/reply paths still resolve, and advance `latestAndroidMsgId`
  // to the new msgId so the dismiss-side `pickAndroidMsgId` can route the most
  // recent Android target. On miss: fall through to prepend (treated as fresh).
  const existing = notifications ?? [];
  let mergeIndex = -1;
  if (next.isUpdate) {
    if (replacedMsgId !== undefined) {
      mergeIndex = existing.findIndex(
        (n) => n.msgId === replacedMsgId || n.latestAndroidMsgId === replacedMsgId
      );
    }
    if (mergeIndex === -1) {
      mergeIndex = existing.findIndex(
        (n) => n.appPackage === next.appPackage && n.title === next.title
      );
    }
  }

  let updatedList: DecryptedNotification[];
  const isMerged = mergeIndex !== -1;
  if (isMerged) {
    const originalMsgId = existing[mergeIndex].msgId;
    const originalIsRead = existing[mergeIndex].isRead;
    updatedList = [...existing];
    // Preserve `isRead` from the original — `next` carries `isRead: false`,
    // and `skipUnreadBump` keeps `unreadCount` flat on merge, so spreading
    // `next.isRead` onto a previously-read card would un-read it without
    // bumping the badge and leave the counter desynced from the panel.
    updatedList[mergeIndex] = {
      ...next,
      msgId: originalMsgId,
      isRead: originalIsRead,
      latestAndroidMsgId: next.msgId,
    };
  } else {
    updatedList = [next, ...existing];
  }
  // Apply the cap unconditionally — merges replace in place so length is
  // preserved today, but pinning the slice here matches apps/extension's
  // shape (background/index.ts:1184) and survives a future merge that adds.
  updatedList = updatedList.slice(0, NOTIFICATION_CAP);
  // Cap appIcon length before caching — defends against malformed
  // payloads bloating IndexedDB. Mirrors extension's check.
  // Write-once per pairing: only cache if no entry exists yet for
  // this package. Icons are stable between app updates and re-pairing
  // clears the cache. Matches the extension's
  // `updatedIconCache[appPackage] === undefined` guard and the web's
  // own `handleConversationMessage` guard in `notifications.ts`.
  const updatedIconCache =
    appIcon && appIcon.length <= APP_ICON_MAX_CHARS && !appIconCache?.[next.appPackage]
      ? { ...(appIconCache ?? {}), [next.appPackage]: appIcon }
      : appIconCache;
  // Skip the unread bump on merge (the user already saw this card; updates
  // refresh content, not unread state). "silent" path also skips the bump —
  // the badge is part of the toast UX the user explicitly muted.
  const skipUnreadBump = isMerged || isSilent;
  const updates: Partial<ReflectoStorage> = {
    notifications: updatedList,
    unreadCount: skipUnreadBump ? (unreadCount ?? 0) : (unreadCount ?? 0) + 1,
    lastSeqId: Math.max(next.seqId, lastSeqId ?? 0),
  };
  if (updatedIconCache !== appIconCache) updates.appIconCache = updatedIconCache;
  await webStorage.set(updates);
}

/**
 * Snapshot fields `applyServerOriginNotification` needs — same
 * pass-through-rather-than-re-read rationale as
 * `ApplyIncomingNotificationOptions`.
 */
export interface ApplyServerOriginNotificationOptions {
  notification: DecryptedNotification;
  /** True when the per-device display gate resolved to `"silent"`. */
  isSilent: boolean;
  notifications: DecryptedNotification[] | undefined;
  unreadCount: number | undefined;
  lastSeqId: number | undefined;
  incomingSeqId: number;
}

/**
 * Apply one public-API (`senderDeviceId === "server"`) notification to the
 * flat `notifications` array: prepend + cap + unread math + the single
 * batched `webStorage.set`. No update-merge — every public-API send is a
 * fresh card (there is no Android `isUpdate`/`replacedMsgId` concept on this
 * path). Called from `notifications.ts`'s `handleServerOriginMessage`, which
 * owns its own decrypt/dedup/display-gate/logging and logs
 * `server_origin_local_add` using the returned `listSize`.
 */
export async function applyServerOriginNotification(
  options: ApplyServerOriginNotificationOptions
): Promise<{ listSize: number }> {
  const { notification, isSilent, notifications, unreadCount, lastSeqId, incomingSeqId } = options;
  const updated = [notification, ...(notifications ?? [])].slice(0, NOTIFICATION_CAP);
  const newUnread = isSilent ? (unreadCount ?? 0) : (unreadCount ?? 0) + 1;
  const newSeqId = Math.max(incomingSeqId, lastSeqId ?? 0);

  await webStorage.set({
    notifications: updated,
    lastSeqId: newSeqId,
    unreadCount: newUnread,
  });
  return { listSize: updated.length };
}
